import {MessageBarType, DefaultButton, PrimaryButton, Stack, StackItem, TextField, Label, Spinner} from "@fluentui/react";
import styles from "../EmailConfiguration/EmailConfiguration.module.css";
import strings from "../../../loc/strings";
import React, { useEffect, useState } from "react";
import { EmailConfigurationItem } from "../../../model/EmailConfigurationItem";
import { displayMessageBar } from "../../LayoutWithAuth/LayoutWithAuth";
import {
    fetchEmailConfiguration,
    sharedEmailsForm
} from "../../../services/EmailConfigurationServices/EmailConfigurationService";
import { useAuth } from "react-oidc-context";

function EmailConfiguration() {
    const auth = useAuth();
    const [sharedEmails, setSharedEmails] = useState<EmailConfigurationItem>(
        {
            caDataManagersSharedEmail: "",
            caEndUsersSharedEmail: "",
            lastUpdatedCADataManagersSharedEmail: undefined,
            lastUpdatedCaEndUsersSharedEmail: undefined
        });
    const [errorMessages, setErrorMessages] = useState<{ caEndUsers?: string; caDataManagers?: string }>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        fetchEmails();
    }, []);

    const fetchEmails = async () => {
        try {
            const emails = await fetchEmailConfiguration(auth.user?.id_token);
            setSharedEmails(emails);
            setIsLoading(false);
        } catch {
            setIsLoading(true);
        }
    };

    const validateEmails = () => {
        const emailRegex =  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i;
        let valid = true;
        const newErrorMessages: { caEndUsers?: string; caDataManagers?: string } = {};

        const caEndUsersEmail = sharedEmails.caEndUsersSharedEmail || "";
        const caDataManagersEmail = sharedEmails.caDataManagersSharedEmail || "";

        if (caEndUsersEmail && !emailRegex.test(caEndUsersEmail)) {
            valid = false;
            newErrorMessages.caEndUsers = strings.emailAddressInvalidMessage;
        }

        if (caDataManagersEmail && !emailRegex.test(caDataManagersEmail)) {
            valid = false;
            newErrorMessages.caDataManagers = strings.emailAddressInvalidMessage;
        }

        setErrorMessages(newErrorMessages);

        return valid;
    };

    const _handleSharedEmails = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        const { name } = event.currentTarget;
        setSharedEmails((prevState) => ({
            ...prevState,
            [name]: newValue || "",
        }));
        setErrorMessages((prevMessages) => ({
            ...prevMessages,
            [name]: undefined
        }));
    };

    const _handleSaveChanges = () => {
        const sharedEmailsItem: EmailConfigurationItem = {
            caDataManagersSharedEmail: sharedEmails.caDataManagersSharedEmail
                ? sharedEmails.caDataManagersSharedEmail.toLowerCase()
                : null,
            caEndUsersSharedEmail: sharedEmails.caEndUsersSharedEmail
                ? sharedEmails.caEndUsersSharedEmail.toLowerCase()
                : null
        };

        return sharedEmailsItem;
    };

    const _onSaveChanges = async () => {
        if (!validateEmails()) {
            return;
        }

        const result = await sharedEmailsForm(_handleSaveChanges(), auth.user?.id_token);

        if (result.messages) {
            const message = result.messages.length > 1
                ? strings.emailAddressAlreadyUsedMessage
                : result.messages[0];

            if (message === strings.CAdataManagerErrorMessageDuplicate) {
                setErrorMessages((prev) => ({ ...prev, caDataManagers: strings.emailAddressAlreadyUsedMessage }));

            } else if (message === strings.CAendUserSharedErrorMessageDuplicate) {
                setErrorMessages((prev) => ({ ...prev, caEndUsers: strings.emailAddressAlreadyUsedMessage }));
            } else {
                result.invalidCharsFlag ?
                        displayMessageBar(MessageBarType.error, result.messages[0], false)
                    :
                        setErrorMessages((prev) => ({ ...prev, caDataManagers: strings.emailAddressAlreadyUsedMessage, caEndUsers: strings.emailAddressAlreadyUsedMessage }));
            }
        } else {
            result.message && displayMessageBar(MessageBarType.success, strings.SharedEmailsSuccessfulUpdateMessage, false);
            setErrorMessages({});
            fetchEmails();
        }
    };

    const onReset = async () => {
        await fetchEmails();
        setErrorMessages({});
    };

    const renderLastUpdatedLabel = (lastUpdatedDate: Date | undefined, errorMessage: string | undefined) => {
        return !errorMessage && lastUpdatedDate ? (
            <div className={styles.lastUpdatedLabel}>
                Last updated: {formatDate(lastUpdatedDate)}
            </div>
        ) : null;
    };

    const formatDate = (lastUpdateDate: Date) => {
        const date = new Date(lastUpdateDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${day}/${month}/${date.getFullYear()} ${hours}:${minutes}`;
    };

    return (
        <Stack>
            <StackItem className={styles.EmailConfigurationHeader}>
                {strings.emailConfiguration}
            </StackItem>
            { isLoading ?
                <Spinner label={strings.requestResultLoading} size={2} styles={{root: {padding: 20}}}></Spinner>
                :
                <Stack horizontal className={styles.EmailConfigurationStack}>
                    <StackItem className={styles.EmailConfigurationItemField}>
                        <TextField
                            label={strings.CAendUserSharedEmailLabel}
                            name={strings.CAendUserSharedEmailName}
                            placeholder={strings.sharedEmailPlaceholder}
                            onChange={_handleSharedEmails}
                            value={sharedEmails.caEndUsersSharedEmail || ""}
                            errorMessage={errorMessages.caEndUsers}
                        />
                        {renderLastUpdatedLabel(sharedEmails.lastUpdatedCaEndUsersSharedEmail, errorMessages.caEndUsers)}
                    </StackItem>
                    <StackItem className={styles.EmailConfigurationItemField}>
                        <TextField
                            label={strings.CAdataManagerSharedEmailLabel}
                            name={strings.CAdataManagerSharedEmailName}
                            placeholder={strings.sharedEmailPlaceholder}
                            onChange={_handleSharedEmails}
                            value={sharedEmails.caDataManagersSharedEmail || ""}
                            errorMessage={errorMessages.caDataManagers}
                        />
                        {renderLastUpdatedLabel(sharedEmails.lastUpdatedCADataManagersSharedEmail, errorMessages.caDataManagers)}
                    </StackItem>
                    <Stack horizontal className={styles.EmailConfigurationStack}>
                        <StackItem>
                            <Label styles={{ root: { color: 'transparent' } }}>&nbsp;</Label>
                            <PrimaryButton
                                text={strings.saveChangesButtonText}
                                onClick={_onSaveChanges}
                            />
                        </StackItem>
                        <StackItem>
                            <Label styles={{ root: { color: 'transparent' } }}>&nbsp;</Label>
                            <DefaultButton
                                text={strings.resetButtonText}
                                onClick={onReset}
                            />
                        </StackItem>
                    </Stack>
                </Stack>
            }
        </Stack>
    );
}

export default EmailConfiguration;
